import { useEffect, useState } from "react";
import { api } from "@/contexts/AuthContext";
import { Download, Search } from "lucide-react";

export default function MembersCRM() {
  const [members, setMembers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [tier, setTier] = useState("");
  const [sort, setSort] = useState("recent");
  const [loading, setLoading] = useState(true);
  const [tiers, setTiers] = useState([]);

  const load = async () => {
    setLoading(true);
    const params = { sort };
    if (q) params.q = q;
    if (tier) params.tier = tier;
    const [m, s] = await Promise.all([
      api.get("/analytics/members", { params }),
      api.get("/analytics/members/summary"),
    ]);
    setMembers(m.data || []);
    setSummary(s.data);
    setLoading(false);
  };

  useEffect(() => { api.get("/tiers").then((r) => setTiers(r.data || [])); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sort, tier]);

  const exportCsv = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/members/export.csv`;
    const tok = sessionStorage.getItem("access_token");
    fetch(url, { headers: { Authorization: `Bearer ${tok}` } })
      .then((r) => r.blob())
      .then((b) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(b);
        link.download = "members.csv";
        link.click();
      });
  };

  return (
    <div className="space-y-6" data-testid="members-crm">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl text-white tracking-wider">Fan CRM</h2>
          <p className="text-white/40 text-xs mt-1">Members · segmented view · export ready.</p>
        </div>
        <button onClick={exportCsv} data-testid="export-members-csv" className="text-xs bg-white/5 hover:bg-[#D4AF37] hover:text-black text-white px-4 py-2 uppercase tracking-widest flex items-center gap-2">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-[#0A0A0A] border border-white/5 rounded p-4" data-testid="stat-total-members">
            <p className="text-[10px] uppercase tracking-widest text-white/40">Total Members</p>
            <p className="text-3xl text-[#D4AF37] font-bold mt-1">{summary.total}</p>
          </div>
          {summary.distribution.map((d) => (
            <div key={d.tier} className="bg-[#0A0A0A] border border-white/5 rounded p-4" data-testid={`stat-tier-${d.tier}`}>
              <p className="text-[10px] uppercase tracking-widest text-white/40">{d.tier}</p>
              <p className="text-3xl text-white font-bold mt-1">{d.count}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input className="input-dark pl-9 w-full" placeholder="Search email, username, name" value={q}
                 data-testid="member-search"
                 onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && load()} />
        </div>
        <select className="input-dark" value={tier} onChange={(e) => setTier(e.target.value)} data-testid="filter-tier">
          <option value="">All tiers</option>
          {tiers.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
        </select>
        <select className="input-dark" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sort-members">
          <option value="recent">Recent</option>
          <option value="top_spender">Top Spenders</option>
          <option value="most_active">Most Active</option>
          <option value="points">Highest Points</option>
        </select>
      </div>

      <div className="bg-[#0A0A0A] border border-white/5 rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
              <th className="text-left py-3 px-4">Email</th>
              <th className="text-left py-3 px-4">Username</th>
              <th className="text-left py-3 px-4">Tier</th>
              <th className="text-right py-3 px-4">Points</th>
              <th className="text-right py-3 px-4">Orders</th>
              <th className="text-right py-3 px-4">Spend</th>
              <th className="text-left py-3 px-4">Joined</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td className="p-6 text-white/40" colSpan={7}>Loading…</td></tr>
            ) : members.length === 0 ? (
              <tr><td className="p-6 text-white/40" colSpan={7}>No members yet.</td></tr>
            ) : members.map((m) => (
              <tr key={m.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`member-row-${m.username}`}>
                <td className="py-3 px-4 text-white">{m.email}</td>
                <td className="py-3 px-4 text-white/60">@{m.username}</td>
                <td className="py-3 px-4"><span className="text-[10px] uppercase tracking-widest text-[#D4AF37]">{m.tier_name || m.tier_slug}</span></td>
                <td className="py-3 px-4 text-right text-white/80">{m.points || 0}</td>
                <td className="py-3 px-4 text-right text-white/60">{m.orders_count || 0}</td>
                <td className="py-3 px-4 text-right text-[#D4AF37]">${(m.lifetime_spend || 0).toFixed(2)}</td>
                <td className="py-3 px-4 text-white/40 text-xs">{m.created_at ? new Date(m.created_at).toLocaleDateString() : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <style>{`
        .input-dark { background:#000; border:1px solid rgba(255,255,255,0.1); color:#fff; padding:10px 12px; font-size:13px; border-radius:4px; outline:none; }
        .input-dark:focus { border-color:#D4AF37; }
      `}</style>
    </div>
  );
}
